import { Box, Button, Typography, styled } from '@mui/material';
import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { FingerRectangle, Image as NImage, Rectangle } from 'neurotec-megamatcherid-management-client';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { THEME } from '../../config';
import ImageDialog from './ImageDialog';
import { SelectionIconButton } from './SelectionIconButton';
import StyledTooltip from '../Misc/StyledTooltip';

const BoxSubjectId = styled(Box)({
    backgroundColor: "#D6E4DF",
    padding: "0.5rem 1rem",
    borderRadius: "5px"
});

const BoxImage = styled(Box)({
    minHeight: "35vh",
    display: "flex",
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    backgroundColor: "#E8E8E8",
    position: "relative"
});

interface IImageCanvasFingerCanvas {
    max: number,
    fingerIndex: number
    setFingerIndex: (count: number) => void
}

interface IImageCanvasProps {
    image?: NImage | null | void;
    isTokenImage?: boolean;
    fingerCanvas?: IImageCanvasFingerCanvas;
    clickable?: boolean;
    rectangle?: Rectangle | FingerRectangle[];
    subjectId?: string;
}

const ImageCanvas: React.FC<IImageCanvasProps> = (props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const divRef = useRef<HTMLElement>(null);
    const [imageDialog, openImageDialog] = useState(false);
    const [showTokenImage, setShowTokenImage] = useState(props.isTokenImage);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const imageSrc = useMemo(() => {
        if (!props.image) return undefined;
        return showTokenImage ? props.image.tokenImage : props.image.image;
    }, [props.image, showTokenImage]);

    const draw = useCallback(() => {
        if (!canvasRef.current || !divRef.current || !imageSrc) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const img = new Image();
        img.src = `data:image/png;base64, ${imageSrc}`;

        img.onload = () => {
            if (!divRef.current) return;

            const aspectRatio = img.width / img.height;
            canvas.width = divRef.current.offsetWidth;
            canvas.height = divRef.current.offsetHeight;
            const canvasAspectRatio = canvas.width / canvas.height;

            let canvasWidth = canvas.width;
            let canvasHeight = canvas.height;
            let rectangleLineWidth;

            if (aspectRatio > canvasAspectRatio) {
                canvasHeight = canvasWidth / aspectRatio;
                rectangleLineWidth = canvasHeight / 85;
            } else {
                canvasWidth = canvasHeight * aspectRatio;
                rectangleLineWidth = canvasWidth / 85;
            }

            const xOffset = (canvas.width - canvasWidth) / 2;
            const yOffset = (canvas.height - canvasHeight) / 2;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, xOffset, yOffset, canvasWidth, canvasHeight);

            if (!showTokenImage && props.rectangle) {
                const widthProportion = img.width / canvasWidth;
                const heightProportion = img.height / canvasHeight;

                ctx.strokeStyle = "green";
                ctx.lineWidth = rectangleLineWidth;

                const drawRect = (rect: Rectangle | undefined, deg: number) => {
                    if (!rect) return;
                    if (rect.x === undefined || rect.y === undefined || rect.width === undefined || rect.height === undefined) return;

                    const rad = (deg * Math.PI) / 180;

                    const scaledX = (rect.x / widthProportion) + xOffset;
                    const scaledY = (rect.y / heightProportion) + yOffset;
                    const scaledW = rect.width / widthProportion;
                    const scaledH = rect.height / heightProportion;

                    const cx = scaledX + scaledW / 2;
                    const cy = scaledY + scaledH / 2;

                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.rotate(rad);
                    ctx.strokeRect(-scaledW / 2, -scaledH / 2, scaledW, scaledH);
                    ctx.restore();
                };

                if (Array.isArray(props.rectangle)) {
                    props.rectangle.forEach(r => {
                        if (r.rotation === undefined) {
                            drawRect(r.boundingRect, 0)
                        } else {
                            let deg = 90 - r.rotation
                            drawRect(r.boundingRect, deg)
                        }
                    });
                } else {
                    drawRect(props.rectangle, 0);
                }
            }
        };
    }, [imageSrc, props.rectangle, showTokenImage]);

    useEffect(() => {
        draw();
        window.addEventListener('resize', draw);
        return () => window.removeEventListener('resize', draw);
    }, [draw]);

    const handleImageSelection = () => {
        if (props.image?.tokenImage) setShowTokenImage(!showTokenImage);
    };

    const handleFingerSelection = (count: number) => {
        if (props.fingerCanvas?.setFingerIndex) props.fingerCanvas.setFingerIndex(count) 
    }

    const handleImageDialogOpen = () => {
        openImageDialog(true)

        buttonRef.current?.blur();
    }

    const getImageArrowSelection = (left: boolean) => (
        props.image?.tokenImage ? (
            <SelectionIconButton
                onClick={handleImageSelection}
                disabled={left ? showTokenImage : !showTokenImage}
                style={{ [left ? 'right' : 'left']: "15px" }}
            >
                <ArrowBackIosNewIcon style={{ color: THEME.palette.text.secondary, transform: left ? "scale(-1)" : "scale(1)" }} />
            </SelectionIconButton>
        ) : 
        props.fingerCanvas? (
            <SelectionIconButton
                onClick={left? () => handleFingerSelection(props.fingerCanvas!.fingerIndex + 1) : () => handleFingerSelection(props.fingerCanvas!.fingerIndex - 1)}
                disabled={left ? props.fingerCanvas.fingerIndex === props.fingerCanvas.max -1 : props.fingerCanvas.fingerIndex === 0}
                style={{ [left ? 'right' : 'left']: "15px" }}
            >
                <ArrowBackIosNewIcon style={{ color: THEME.palette.text.secondary, transform: left ? "scale(-1)" : "scale(1)" }} />
            </SelectionIconButton>
        ) : null
    );

    return (
        <Box style={{ height: "100%" }}>
            {props.subjectId && props.subjectId !== "None" && (
                <StyledTooltip title={props.subjectId}>
                    <BoxSubjectId>
                        <b>&nbsp;&nbsp;Subject Id:</b> {props.subjectId.length < 35 ? props.subjectId : `${props.subjectId.substring(0, 30)}...`}
                    </BoxSubjectId>
                </StyledTooltip>
            )}
            <BoxImage ref={divRef}>
                {props.image?.image ? (
                    <Box style={{ position: "absolute", alignItems: "center", display: "flex" }}>
                        {getImageArrowSelection(true)}
                        {getImageArrowSelection(false)}
                        {props.clickable ? (
                            <Button ref={buttonRef} onClick={handleImageDialogOpen} disableRipple>
                                <canvas ref={canvasRef} style={{ maxWidth: "100%" }} />
                            </Button>
                        ) : (
                            <canvas ref={canvasRef} style={{ maxWidth: "100%" }} />
                        )}
                        <ImageDialog image={props.image} open={imageDialog} onClose={() => openImageDialog(false)} isTokenImage={showTokenImage} />
                    </Box>
                ) : props.image !== undefined ? (
                    <>
                    <Typography>No image</Typography>
                    {props.fingerCanvas? getImageArrowSelection(true): null}
                    {props.fingerCanvas? getImageArrowSelection(false): null}
                    </>
                ) : (
                    <Typography>Loading...</Typography>
                )}
            </BoxImage>
        </Box>
    );
};

export default ImageCanvas;